import React, { useState, useEffect } from 'react'
import { Modal } from '../../_EOS/Modal'
import { UnifiedDispatchModal } from '../UnifiedDispatchModal/UnifiedDispatchModal'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { getAvailableStocks } from '../../../pages/DispatchOtNew/config/api'
import { CompleteWorkOrderUpdateDto } from '../../../pages/DispatchOtNew/entities/DispatchOt'
import { ApiConfig } from '../../../config/api'
import axios from 'axios'

interface UnifiedModalState {
  activeStep: number
  qtyIncident: number
  selectedIncidentTypeId: number
  availableStocks: any[]
  isLoadingStocks: boolean
  selectedBatchId: number | undefined // Agregado para manejar el lote seleccionado
}

interface UnifiedDispatchModalWrapperProps {
  isOpen: boolean
  onClose: () => void
  selectedRow: any
  typeInventoryIncident: any[]
  workOrderId: number
  userId: number
  showNotification: (message: string, success: boolean) => void
  onSuccess: () => void // Mantener para recargar la data
}

const postSaveCompleteWorkOrder = (body: CompleteWorkOrderUpdateDto) => {
  const url = new URL(`${ApiConfig.WorkOrderApi}/SaveCompleteWorkOrder`)

  return axios.post(url.toString(), body)
}

export const UnifiedDispatchModalWrapper: React.FC<
  UnifiedDispatchModalWrapperProps
> = ({
  isOpen,
  onClose,
  selectedRow,
  typeInventoryIncident,
  workOrderId,
  userId,
  showNotification,
  onSuccess
}) => {
  // Estados internos del modal
  const [unifiedModalState, setUnifiedModalState] = useState<UnifiedModalState>(
    {
      activeStep: 0,
      qtyIncident: 0,
      selectedIncidentTypeId: 0,
      availableStocks: [],
      isLoadingStocks: false,
      selectedBatchId: undefined
    }
  )

  const { state: saveState, callApi: saveCompleteDispatchCallApi } = useApi()

  // Hook para cargar stocks disponibles
  const { state: stocksState, callApi: callStocksApi } = useApi<any>()

  // Resetear estado cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setUnifiedModalState({
        activeStep: 0,
        qtyIncident: 0,
        selectedIncidentTypeId: 0,
        availableStocks: [],
        isLoadingStocks: false,
        selectedBatchId: selectedRow?.batch?.batchId
    })
  }
}, [isOpen, selectedRow])

  // Efecto para manejar la respuesta de stocks disponibles
  useEffect(() => {
    if (stocksState.status === Status.Success && stocksState.data) {
      const stocks = stocksState.data.stocks || []

      if (stocks.length === 0) {
        // No cerrar el modal, solo mostrar que no hay stocks disponibles
        setUnifiedModalState((prev) => ({
          ...prev,
          availableStocks: [],
          isLoadingStocks: false
        }))
        return
      }

      const stocksWithState = stocks.map((stock: any) => ({
        ...stock,
        currentQty: 0,
        isSelected: false
      }))

      setUnifiedModalState((prev) => ({
        ...prev,
        availableStocks: stocksWithState,
        isLoadingStocks: false
      }))
    } else if (stocksState.status === Status.Error) {
      setUnifiedModalState((prev) => ({ ...prev, isLoadingStocks: false }))
      showNotification('Error al cargar posiciones disponibles', false)
    }
  }, [stocksState.status, stocksState.data, showNotification])


  // Función para cargar stocks disponibles
  const loadAvailableStocks = () => {
    if (!selectedRow || !workOrderId) {
      showNotification(
        'Error: No se pudo obtener la información necesaria',
        false
      )
      return
    }

    setUnifiedModalState((prev) => ({ ...prev, isLoadingStocks: true }))
    callStocksApi(getAvailableStocks(workOrderId, selectedRow.skuId))
  }

  // Callbacks para el modal unificado
  const handleIncidentQtyChange = (qty: number) => {
    setUnifiedModalState((prev) => ({
      ...prev,
      qtyIncident: Math.max(0, qty)
    }))
  }

  const handleIncidentTypeChange = (typeId: number) => {
    setUnifiedModalState((prev) => ({
      ...prev,
      selectedIncidentTypeId: typeId
    }))
  }

  const handleStockQtyChange = (stockIndex: number, qty: number) => {
    setUnifiedModalState((prev) => {
      const newStocks = [...prev.availableStocks]
      newStocks[stockIndex] = {
        ...newStocks[stockIndex],
        currentQty: Math.max(0, qty),
        isSelected: qty > 0
      }

      return {
        ...prev,
        availableStocks: newStocks
      }
    })
  }

  const handleNext = () => {
    if (unifiedModalState.activeStep === 0) {
      // En el primer paso, cargar stocks y avanzar
      setUnifiedModalState((prev) => ({
        ...prev,
        isLoadingStocks: true,
        activeStep: prev.activeStep + 1
      }))
      loadAvailableStocks()
    } else if (
      unifiedModalState.activeStep === 1 &&
      unifiedModalState.availableStocks.length === 0
    ) {
      // Si estamos en el paso 1 y no hay stocks cargados, cargarlos
      setUnifiedModalState((prev) => ({
        ...prev,
        isLoadingStocks: true
      }))
      loadAvailableStocks()
    } else {
      // Para otros pasos, avanzar normalmente
      setUnifiedModalState((prev) => ({
        ...prev,
        activeStep: prev.activeStep + 1
      }))
    }
  }

  const handleBack = () => {
    setUnifiedModalState((prev) => ({
      ...prev,
      activeStep: prev.activeStep - 1
    }))
  }

  const handleCancel = () => {
    onClose()
  }

  const requestSaveCompleteWorkOrder = (
    completeWorkOrderDto: CompleteWorkOrderUpdateDto
  ) => {
    saveCompleteDispatchCallApi(postSaveCompleteWorkOrder(completeWorkOrderDto))
      .then(() => {
        showNotification('Operación completada correctamente', true)
        onSuccess()
        onClose()
      })
      .catch(() => {
        showNotification('Error al completar la operación', false)
      })
  }

  const handleSave = () => {
    const completeWorkOrderDto: CompleteWorkOrderUpdateDto = {
      workOrderId: workOrderId,
      userId: userId,
      skuId: selectedRow.skuId,
      itemsMovements: unifiedModalState.availableStocks
        .filter((stock) => stock.isSelected)
        .map((stock) => ({
          positionId: stock.positionId,
          quantity: stock.currentQty,
          batchId: stock.batch?.batchId || null
        })),
      itemIncident: {
        batchId: unifiedModalState.selectedBatchId, // Usar el lote seleccionado
        typeInventoryIncidentId: unifiedModalState.selectedIncidentTypeId,
        incidentQuantity: unifiedModalState.qtyIncident
      }
    }

    // Llamar al endpoint SaveCompleteWorkOrder
    requestSaveCompleteWorkOrder(completeWorkOrderDto)
  }

  if (!selectedRow) return null

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      cancelButton={{ title: 'Cancelar', action: handleCancel }}
      maxWidth='md'
    >
      <UnifiedDispatchModal
        skuInfo={{
          skuId: selectedRow.skuId,
          skuName: selectedRow.skuName,
          sku: selectedRow.sku.toString(),
          url: selectedRow.url || '',
          batch: selectedRow.batch
        }}
        incidentTypes={typeInventoryIncident}
        qtyIncident={unifiedModalState.qtyIncident}
        selectedIncidentTypeId={unifiedModalState.selectedIncidentTypeId}
        selectedBatchId={unifiedModalState.selectedBatchId}
        availableStocks={unifiedModalState.availableStocks}
        isLoadingStocks={
          unifiedModalState.isLoadingStocks ||
          stocksState.status === Status.Loading
        }
        isLoading={saveState.status === Status.Loading}
        onIncidentQtyChange={handleIncidentQtyChange}
        onIncidentTypeChange={handleIncidentTypeChange}
        onBatchChange={() => {}} // Función vacía ya que no es necesaria
        onStockQtyChange={handleStockQtyChange}
        onNext={handleNext}
        onBack={handleBack}
        onCancel={handleCancel}
        onSave={handleSave}
        activeStep={unifiedModalState.activeStep}
      />
    </Modal>
  )
}
